/**
 * componentNodeFactory.js
 *
 * Single source of truth for ALL component node creation.
 * This factory ensures consistent initialization across all creation paths:
 * - LeftPanel manual mode
 * - LeftPanel chain mode
 * - MainLayout paste
 * - MainLayout clone
 * - MainLayout hotkey add
 * - MainLayout insert between
 * - CatalogComponentModal auto-add
 */

import { COMPONENT_LIBRARY } from '../data/componentLibrary';
import { initializeDataSourceValues } from '../data/dataSourceParameters';

/**
 * Generate a unique node ID
 * @returns {string} Unique node ID
 */
export function generateNodeId() {
  return `node-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Deep clone an object (safe for parameters with nested objects)
 * @param {Object} obj - Object to clone
 * @returns {Object} Deep cloned object
 */
function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Get component definition from library or use provided definition
 * @param {string|Object} componentDefOrType - Component type string or full definition object
 * @param {Object} catalogComponents - Optional catalog components lookup
 * @returns {Object|null} Component definition or null if not found
 */
function resolveComponentDef(componentDefOrType, catalogComponents = {}) {
  if (typeof componentDefOrType === 'string') {
    // Look up by type string
    return COMPONENT_LIBRARY[componentDefOrType] || catalogComponents[componentDefOrType] || null;
  }
  // Already a component definition object
  return componentDefOrType;
}

/**
 * Initialize parameters for a component
 * Handles both generic and catalog components correctly
 *
 * @param {Object} componentDef - Component definition
 * @returns {Object} Initialized parameters
 */
function initializeParameters(componentDef) {
  // Deep clone parameters to avoid reference issues
  const params = deepClone(componentDef.parameters || {});

  // For catalog components, initialize data source values
  if (componentDef.isCatalog) {
    return initializeDataSourceValues(componentDef, params);
  }

  return params;
}

/**
 * Build the data object for a node
 * Ensures all required metadata is set consistently
 *
 * @param {Object} componentDef - Component definition
 * @returns {Object} Node data object
 */
function buildNodeData(componentDef) {
  const nodeData = {
    name: componentDef.isCatalog ? componentDef.partNumber : componentDef.displayName,
    parameters: initializeParameters(componentDef)
  };

  // Add catalog-specific metadata
  if (componentDef.isCatalog) {
    nodeData.isCatalog = true;
    nodeData.id = componentDef.id; // Database ID for API calls (S2P, freq data)
    nodeData.partNumber = componentDef.partNumber;
    nodeData.manufacturer = componentDef.manufacturer;
    nodeData.baseType = componentDef.baseType;
    nodeData.baseTypeName = COMPONENT_LIBRARY[componentDef.baseType]?.displayName || componentDef.baseType;
    nodeData.hasFreqData = componentDef.hasFreqData || false;
    nodeData.hasS2P = componentDef.hasS2P || false;
  }

  return nodeData;
}

/**
 * Creates a new component node with proper initialization
 * This is the SINGLE SOURCE OF TRUTH for creating new nodes.
 *
 * @param {string|Object} componentDefOrType - Component definition or type string
 * @param {Object} options - Creation options
 * @param {Object} options.position - {x, y} position for the node
 * @param {Object} options.catalogComponents - Optional catalog components lookup for type resolution
 * @param {string} options.nodeType - Optional node type override (defaults to componentDef.type)
 * @param {string} options.nodeId - Optional custom node ID (generates one if not provided)
 * @returns {Object|null} Complete node object ready for addition to canvas, or null if invalid
 */
export function createComponentNode(componentDefOrType, options = {}) {
  const {
    position = { x: 300, y: 200 },
    catalogComponents = {},
    nodeType = null,
    nodeId = null
  } = options;

  // Resolve component definition
  const componentDef = resolveComponentDef(componentDefOrType, catalogComponents);

  if (!componentDef) {
    console.error('createComponentNode: Component not found:', componentDefOrType);
    return null;
  }

  // Determine the node type
  const resolvedNodeType = nodeType || componentDef.type;

  // Build the complete node object
  const node = {
    id: nodeId || generateNodeId(),
    type: resolvedNodeType,
    position: { ...position },
    data: buildNodeData(componentDef)
  };

  // Add drag handle restriction (notes can be dragged from anywhere, others only by title bar)
  if (resolvedNodeType !== 'note') {
    node.dragHandle = '.drag-handle';
  }

  return node;
}

/**
 * Creates a cloned node from a source node (for paste/clone operations)
 * Re-initializes data source values for catalog components to ensure consistency
 *
 * @param {Object} sourceNode - The node to clone
 * @param {Object} options - Clone options
 * @param {Object} options.position - Optional new position (uses source position with offset if not provided)
 * @param {number} options.offsetX - X offset from source position (default: 50)
 * @param {number} options.offsetY - Y offset from source position (default: 50)
 * @returns {Object} Cloned node with new ID and proper initialization
 */
export function cloneComponentNode(sourceNode, options = {}) {
  const {
    position = null,
    offsetX = 50,
    offsetY = 50
  } = options;

  // Deep clone the entire source node
  const clonedNode = deepClone(sourceNode);

  // Generate new unique ID
  clonedNode.id = generateNodeId();

  // Update position
  if (position) {
    clonedNode.position = { ...position };
  } else {
    clonedNode.position = {
      x: sourceNode.position.x + offsetX,
      y: sourceNode.position.y + offsetY
    };
  }

  // Re-initialize data source values for catalog components
  // This ensures proper data source flags and values are set
  if (clonedNode.data?.isCatalog && clonedNode.data?.parameters) {
    // Create a component-like object for initializeDataSourceValues
    const componentData = {
      partNumber: clonedNode.data.partNumber,
      manufacturer: clonedNode.data.manufacturer,
      baseType: clonedNode.data.baseType,
      hasFreqData: clonedNode.data.parameters.hasFreqData?.value || false,
      hasS2P: clonedNode.data.parameters.hasS2PData?.value || false,
      isCatalog: true
    };

    clonedNode.data.parameters = initializeDataSourceValues(
      componentData,
      clonedNode.data.parameters
    );
  }

  return clonedNode;
}

/**
 * Creates multiple cloned nodes (for multi-select paste operations)
 * Maintains relative positions between nodes
 *
 * @param {Array} sourceNodes - Array of nodes to clone
 * @param {Object} options - Clone options
 * @param {Object} options.baseOffset - {x, y} offset for all cloned nodes
 * @returns {Array} Array of cloned nodes with new IDs
 */
export function cloneMultipleNodes(sourceNodes, options = {}) {
  const { baseOffset = { x: 50, y: 50 } } = options;

  // Create mapping of old IDs to new IDs for edge reconnection
  const idMapping = new Map();

  const clonedNodes = sourceNodes.map(sourceNode => {
    const clonedNode = cloneComponentNode(sourceNode, {
      offsetX: baseOffset.x,
      offsetY: baseOffset.y
    });

    idMapping.set(sourceNode.id, clonedNode.id);
    return clonedNode;
  });

  return { clonedNodes, idMapping };
}

/**
 * Updates edges to use new node IDs after cloning
 * Used when pasting multiple nodes with their connections
 *
 * @param {Array} edges - Original edges between nodes
 * @param {Map} idMapping - Mapping of old node IDs to new node IDs
 * @returns {Array} Updated edges with new IDs
 */
export function remapEdges(edges, idMapping) {
  return edges
    .filter(edge => idMapping.has(edge.source) && idMapping.has(edge.target))
    .map(edge => ({
      ...edge,
      id: `edge-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      source: idMapping.get(edge.source),
      target: idMapping.get(edge.target)
    }));
}

const componentNodeFactory = {
  createComponentNode,
  cloneComponentNode,
  cloneMultipleNodes,
  remapEdges,
  generateNodeId
};

export default componentNodeFactory;
